/**
 * 語系純值模組（無 React、無 hooks）——client／server／prerender 三棧共用。
 * 從 i18n/index.tsx 拆出（C 階段 2026-10-10）：index.tsx 標 'use client' 後，
 * server 端（src/lib/seo.ts、generateMetadata）改由呢度 import DICT_BY_CODE。
 */
import zhHK from './locales/zh-HK';
import { LOCALES, LOCALE_STORAGE_KEY, LEGACY_STORAGE_KEY, LEGACY_LANG_MAP } from './locales';

/** zh-HK 基線字典推導的全站文案型別（couponly 核心技巧） */
type Widen<T> =
  T extends (...args: infer A) => infer R ? (...args: A) => string
  : T extends string ? string
  : T extends boolean ? boolean
  : T extends number ? number
  : T extends object ? { [K in keyof T]: Widen<T[K]> }
  : T;
export type Dict = Widen<typeof zhHK>;

/** code → dictionary（非 React 環境也可按 locale 取字典） */
export const DICT_BY_CODE: Record<string, Dict> = Object.fromEntries(
  LOCALES.map((l) => [l.code, l.dict as Dict])
);
export const META_BY_CODE = Object.fromEntries(LOCALES.map((l) => [l.code, l]));
export { LOCALES, LOCALE_STORAGE_KEY, LEGACY_STORAGE_KEY, LEGACY_LANG_MAP };

/** navigator.language 語系對映（已登記語言優先；未登記回落 zh-HK/en） */
export function mapNavigatorLang(lang: string): string {
  const l = (lang || '').toLowerCase();
  if (l.startsWith('zh')) return /tw|hk|mo|hant/.test(l) || l === 'zh' ? 'zh-HK' : 'zh-CN';
  if (l.startsWith('ms')) return 'ms';
  if (l.startsWith('ar')) return 'ar';
  if (l.startsWith('es')) return 'es';
  return 'en';
}

/** 任意輸入（URL param／localStorage 值）→ 已登記 locale；對不上回 null */
export function resolveLocale(input: string | null | undefined): string | null {
  if (!input) return null;
  const raw = input.trim();
  const lower = raw.toLowerCase();
  const direct = LOCALES.find((l) => l.code.toLowerCase() === lower);
  if (direct) return direct.code;
  const prefix = LOCALES.find((l) => lower.startsWith(l.code.toLowerCase() + '-'));
  if (prefix) return prefix.code;
  const mapped = mapNavigatorLang(raw);
  return DICT_BY_CODE[mapped] ? mapped : null;
}
